import { Download, Gear, PenNib } from "@phosphor-icons/react"
import { Chart } from "@tokenterminal/core/feature/chart/Chart"
import { withChartContext } from "@tokenterminal/core/feature/chart/tt-ui-chart/ChartContext"
import { type GranularityType } from "@tokenterminal/core/feature/chart/tt-ui-chart/utils/groupDataBasedOnGranularity"
import { IconButton } from "@tokenterminal/core/ui/button/IconButton"
import { Heading } from "@tokenterminal/core/ui/heading/Heading"
import { MenuButton, MenuItem } from "@tokenterminal/core/ui/menu/MenuButton"
import { LoadingSpinner } from "@tokenterminal/core/ui/progress/LoadingSpinner"
import { type Interval } from "@tokenterminal/tt-analytics-api-types/dist/api/zoom"
import { ClientOnlySuspense } from "components/ClientOnlySuspense"
// import { CopyChartAsImageModal } from "components/CopyChartAsImageModal/CopyChartAsImageModal"
import { ErrorBoundary } from "components/ErrorBoundary"
import { atom } from "jotai"
import { type CustomChartConfigPayload } from "modules/chart-builder/types"
import { LazyProPaywallModal } from "modules/paywall/LazyProPaywallModal"
import { useMetricsConfigurationList } from "modules/projects/hooks/useMetricsConfigurationList"
import { useStudioLink } from "modules/projects/hooks/useStudioLink"
import useChartDownloadAction from "modules/projects/MetricsTab/useChartDownloadAction"
import { useMemo, useState } from "react"
import { SidebarLegend } from "./SidebarLegend"
import { useProjectsLegendData } from "./useProjectsLegendData"

// Number of top projects to show in the chart and legend
const MAX_PROJECTS = 10

/**
 * Lowercases the first letter of a metric name, unless more than the first letter is uppercase. If more than
 * the first letter is uppercase, we can assume it's some kind of abbreviation and should be left as is.
 */

const lowecaseStringForSentenceCase = (value: string) => {
  const firstLetter = value.charAt(0)
  const restOfString = value.slice(1)

  // If the first letter is lowercase, we're already in sentence case.
  const isFirstLetterLowercase = firstLetter === firstLetter.toLowerCase()
  // If the second letter is uppercase, we can assume it's some kind of abbreviation.
  const isSecondLetterUppercase =
    restOfString.length > 0 &&
    restOfString.charAt(0) === restOfString.charAt(0).toUpperCase()

  if (isFirstLetterLowercase || isSecondLetterUppercase) {
    return value
  }

  return firstLetter.toLowerCase() + restOfString
}

const useChartConfig = ({
  interval,
  title,
  subtitle,
  metricSlug,
  granularity,
  isStatic,
  isEcosystemMetric,
  maxProjects,
}: {
  interval: Interval
  title: string
  subtitle: string
  metricSlug: string
  granularity: GranularityType
  isStatic: boolean
  isEcosystemMetric: boolean
  maxProjects: number
}): CustomChartConfigPayload & {
  id: string
} => {
  return useMemo(() => {
    return {
      id: "some string",
      zoom: interval,
      title,
      description: subtitle,
      shared: true,
      configs: [
        {
          id: "b8cfbcd5-cdac-43a6-8944-453452gw",
          title: "",
          yAxis: "left",
          colors: [],
          metric: metricSlug,
          display: null,
          filters: [
            {
              type: "project",
              values: [],
              comparator: "in",
            },
          ],
          groupBy: isEcosystemMetric ? "chain" : ("project" as const),
          projects: [],
          chart_type: "bar-stacked",
          limit: maxProjects,
        },
      ],
      show_table: false,
      show_title: false,
      granularity,
      show_description: false,
      show_current_period: isStatic ? true : false,
    }
  }, [
    interval,
    title,
    subtitle,
    metricSlug,
    granularity,
    isStatic,
    isEcosystemMetric,
    maxProjects,
  ])
}

const MarketShareAreaChart = withChartContext(
  ({
    metricSlug,
    granularity,
    interval,
    isStatic,
    metricTitle,
    isEcosystemMetric,
    maxProjects = MAX_PROJECTS,
  }: {
    metricSlug: string
    granularity: GranularityType
    interval: Interval
    isStatic: boolean
    metricTitle: string
    isEcosystemMetric: boolean
    maxProjects?: number
  }) => {
    // const isCopyChartImagesEnabled = useFeatureFlagEnabled("copy_chart_images")
    const [isProPaywallModalOpen, setIsProPaywallModalOpen] = useState(false)
    // const [isChartImageModalOpen, setIsChartImageModalOpen] = useState(false)
    const studioLink = useStudioLink()

    const lowercasedMetricTitle = lowecaseStringForSentenceCase(metricTitle)
    const title = `Top ${maxProjects} projects by ${lowercasedMetricTitle}`
    let subtitle
    // Create a descriptive subtitle based on the metric type and interval
    if (isStatic) {
      subtitle = `Average weekly ${lowercasedMetricTitle} for the last 365 days`
    } else {
      subtitle = `Weekly sum of ${lowercasedMetricTitle} for the last 365 days`
    }

    const { data: metricConfigurations = [] } = useMetricsConfigurationList()
    const metricConfiguration = metricConfigurations.find(
      (m) => m.slug === metricSlug
    )

    const chartConfig = useChartConfig({
      interval,
      title,
      subtitle,
      metricSlug,
      granularity,
      isStatic,
      isEcosystemMetric,
      maxProjects,
    })

    const { legendItems } = useProjectsLegendData({
      metricSlug,
      interval,
      limit: maxProjects,
    })

    const { downloadChart } = useChartDownloadAction({
      setIsProPaywallModalOpen,
      title: chartConfig.title,
      filters: {
        metric_id: metricSlug,
        interval,
        granularity,
      },
    })

    return (
      <div className="flex flex-col h-full">
        <div className="flex flex-col md:flex-row items-start justify-between">
          <div className="flex flex-col gap-0 mb-4">
            <Heading level={4} className="text-pretty">
              {title}
            </Heading>
            <p className="text-sm text-fg-secondary"> {subtitle}</p>
          </div>
          <div className="hidden md:flex flex-row items-end gap-px -mt-2 -mr-2">
            <MenuButton
              placement="bottom end"
              trigger={
                <IconButton icon={Gear} label="Actions" variant="ghost" />
              }
            >
              <MenuItem onAction={() => studioLink(chartConfig)} icon={PenNib}>
                Open in studio
              </MenuItem>
              {/* Our sheets plugin doesn't currently support these types of charts */}
              {/* <MenuItem
                icon={Function}
                onAction={() => setIsFormulaModalOpen(true)}
              >
                Sheets formula
              </MenuItem> */}
              <MenuButton.Item icon={Download} onAction={downloadChart}>
                Download as CSV
              </MenuButton.Item>

              {/* {isCopyChartImagesEnabled && (
                <MenuButton.Item
                  icon={Copy}
                  onAction={() => setIsChartImageModalOpen(true)}
                >
                  Copy as image
                </MenuButton.Item>
              )} */}
            </MenuButton>
          </div>
        </div>
        {/* Min height helps us avoid layout shift when the chart is loading in */}
        <div className="flex flex-col md:flex-row gap-6 flex-1 min-h-[345px]">
          <div className="flex-grow">
            <ErrorBoundary
              fallback={
                <div className="h-[320px] md:h-full flex items-center justify-center text-fg-secondary text-sm">
                  Error loading competitive landscape
                </div>
              }
            >
              <ClientOnlySuspense
                fallback={
                  <div className="w-full h-[320px] md:h-full flex items-center justify-center">
                    <LoadingSpinner diameter={24} />
                  </div>
                }
              >
                <div className="w-full h-[320px] md:h-full">
                  <Chart
                    configAtom={atom(chartConfig)}
                    showTitle={false}
                    showDescription={false}
                    isVisible
                    hideYAxisLabels
                    showLegend={false}
                  />
                </div>
              </ClientOnlySuspense>
            </ErrorBoundary>
          </div>
          <div className="flex flex-col justify-start gap-8 w-full md:w-[225px]">
            {legendItems.length > 0 ? (
              <SidebarLegend
                items={legendItems}
                metric={metricConfiguration}
                interval={interval}
                metricTitle={metricTitle}
              />
            ) : null}
          </div>
        </div>
        <LazyProPaywallModal
          type="download_chart"
          featureName="Downloading chart data"
          isOpen={isProPaywallModalOpen}
          onClose={() => setIsProPaywallModalOpen(false)}
          setIsOpen={setIsProPaywallModalOpen}
        />
        {/* <CopyChartAsImageModal
          isOpen={isChartImageModalOpen}
          setIsOpen={setIsChartImageModalOpen}
          chartConfig={chartConfig}
          renderContent={() => (
            <div className="h-full flex flex-col">
              <div className="flex flex-col gap-0 mb-4">
                <Heading level={4}>{title}</Heading>
                <p className="text-sm text-fg-secondary"> {subtitle}</p>
              </div>
              <ErrorBoundary
                fallback={
                  <div className="h-full flex items-center justify-center text-fg-secondary text-sm">
                    Error loading competitive landscape
                  </div>
                }
              >
                <ClientOnlySuspense
                  fallback={
                    <div className="w-full h-full flex items-center justify-center">
                      <LoadingSpinner diameter={24} />
                    </div>
                  }
                >
                  <div className="h-full relative">
                    <Chart
                      configAtom={atom(chartConfig)}
                      showTitle={false}
                      showDescription={false}
                      isVisible
                      hideYAxisLabels
                      showLegend={true}
                    />
                  </div>
                </ClientOnlySuspense>
              </ErrorBoundary>
            </div>
          )}
        /> */}
      </div>
    )
  }
)

export default MarketShareAreaChart
